import { syntaxHighlighter } from './syntaxHighlighter'

/**
 * Do not run in production, contains possibly dangerous code
 */
const updateTable = (
  key: string,
  type: 'rewrite' | 'append',
  ...createTableProps: Parameters<typeof updateTableContent>
) => {
  const selector = document.querySelector(`[data-fu-tools-table-content=${key}]`)
  if (!selector) {
    return undefined
  }

  if (type === 'rewrite') {
    selector.innerHTML = updateTableContent(...createTableProps)
    return undefined
  }

  selector.insertAdjacentHTML('afterbegin', updateTableContent(...createTableProps))
  return undefined
}

const formatBadge = (value?: string | null) => ({
  raw: value ?? '0',
  formatted: Number(value ?? '0') > 99 ? '9+' : String(Number(value ?? '0')),
  rawIncremented: String(Number(value) + 1),
  formattedIncremented: Number(value ?? '0') + 1 > 99 ? '9+' : String(Number(value ?? '0') + 1),
})

const incrementNotificationBadge = (key: string) => {
  // menu badge
  const menuBadge = document.querySelector(
    `[data-fu-tools-menu="${key}"] [data-fu-tools-menu-badge]`
  )
  const formattedMenuBadge = formatBadge(menuBadge?.getAttribute('data-fu-tools-menu-badge-raw'))
  menuBadge?.setAttribute('data-fu-tools-menu-badge', formattedMenuBadge.formattedIncremented)
  menuBadge?.setAttribute('data-fu-tools-menu-badge-raw', formattedMenuBadge.rawIncremented)

  // main badge
  const badge = document.querySelector(`[data-fu-tools-badge]`)
  const formattedBadge = formatBadge(badge?.getAttribute('data-fu-tools-badge-raw'))

  badge?.setAttribute('data-fu-tools-badge', formattedBadge.formattedIncremented)
  badge?.setAttribute('data-fu-tools-badge-raw', formattedBadge.rawIncremented)
}

const clearNotificationBadge = (key: string) => {
  const menuBadge = document.querySelector(
    `[data-fu-tools-menu="${key}"] [data-fu-tools-menu-badge]`
  )
  menuBadge?.setAttribute('data-fu-tools-menu-badge', '')
  menuBadge?.setAttribute('data-fu-tools-menu-badge-raw', '')

  const allMenuBadges = document.querySelectorAll(`[data-fu-tools-menu-badge-raw]`)
  const badgeCount = Array.from(allMenuBadges).reduce((acc, badge) => {
    const value = badge.getAttribute('data-fu-tools-menu-badge-raw')
    return acc + (value ? Number(value) : 0)
  }, 0)

  document
    .querySelector(`[data-fu-tools-badge]`)
    ?.setAttribute(
      'data-fu-tools-badge',
      badgeCount === 0 ? '' : badgeCount > 99 ? '9+' : String(badgeCount)
    )
  document
    .querySelector(`[data-fu-tools-badge-raw]`)
    ?.setAttribute('data-fu-tools-badge-raw', String(badgeCount))
}

const updateTableContent = ({
  rows,
  isRowClickable = false,
}: {
  rows: {
    dataAttributes?: Record<string, string | number | Record<string, unknown> | unknown[]>
    data: (string | { render: (column: unknown[]) => string })[]
  }[]
  isRowClickable?: boolean
}) => `${rows
  .map(
    row => `
      <div fu-tools-table-row class="[&:not([data-fu-tools-row-visible])]:flex data-[fu-tools-row-visible=visible]:flex data-[fu-tools-row-visible=hidden]:hidden ${isRowClickable ? 'cursor-pointer hover:bg-[#364153] transition duration-200' : ''}"
      ${Object.entries(row.dataAttributes ?? {})
        .map(
          ([key, value]) =>
            `${key}="${typeof value === 'string' || typeof value === 'number' ? helpers.sanitizeStringQuotes(String(value)) : helpers.sanitizeStringQuotes(helpers.convertObjectToString(value))}"`
        )
        .join(' ')}>
        ${row.data.map(cell => (typeof cell === 'string' ? renderTableCell(cell) : cell.render(row.data))).join('')}
      </div>`
  )
  .join('')}
`

const renderTableCell = (title: string) => {
  const div = document.createElement('div')
  div.className =
    'px-4 py-2 whitespace-nowrap text-sm text-white w-full overflow-hidden text-ellipsis'
  div.setAttribute('title', title)
  div.textContent = title
  return div.outerHTML
}

const showModal = (documentFragment: DocumentFragment) => {
  const dialog = document.querySelector('#fu-tools-dialog')
  const dialogContent = document.querySelector('#fu-tools-dialog-content')
  if (!(dialogContent instanceof HTMLDivElement) || !(dialog instanceof HTMLDialogElement)) {
    return
  }

  dialogContent.innerHTML = ''
  dialogContent.appendChild(documentFragment)
  dialog.show()
}

/**
 * Do not run in production, contains possibly dangerous code
 */
const createHTMLElement = ({
  className = '',
  content = '',
  id = '',
  attributes = {},
  title = '',
  element = 'div',
  copyOnClick = false,
  dangerouslySetInnerHTML = false,
}: {
  className?: string
  content?: string | (() => DocumentFragment)
  id?: string
  attributes?: Record<string, string | number>
  title?: string
  element?: string
  copyOnClick?: boolean
  dangerouslySetInnerHTML?: boolean
} = {}) => {
  const el = document.createElement(element)
  el.className = className
  Object.entries(attributes).forEach(([key, value]) => el.setAttribute(key, String(value)))
  const resolvedContent = typeof content === 'function' ? content() : content
  if (resolvedContent instanceof DocumentFragment) {
    el.appendChild(resolvedContent)
  } else if (dangerouslySetInnerHTML) {
    el.innerHTML = resolvedContent
  } else {
    el.textContent = resolvedContent
  }
  if (id) {
    el.id = id
  }
  if (title) {
    el.title = title
  }
  if (copyOnClick) {
    el.onclick = () => navigator.clipboard.writeText(el.textContent ?? '')
  }
  return el
}

const createHTMLElements = (data: Parameters<typeof createHTMLElement>[0][]) => {
  const documentFragment = document.createDocumentFragment()
  data.forEach(params => {
    const div = createHTMLElement(params)
    documentFragment.appendChild(div)
  })
  return documentFragment
}

const sanitizeHtml = (text: string) =>
  text.replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c] ?? '')

// @deperecated - get rid of this
const sanitizeStringQuotes = (text: string) => text?.replace(/'/g, '&#39;').replace(/"/g, '&quot;')
const replaceNewlinesToHTML = (text: string) => text?.replace(/\n/g, '<br />')

const convertObjectToString = (data: Record<string, unknown> | unknown[]) =>
  JSON.stringify(data, null, 2)
const convertObjectToSanitizedDialogHTML = (data: Record<string, unknown>) =>
  replaceNewlinesToHTML(
    syntaxHighlighter.json(
      sanitizeHtml(
        convertObjectToString(data)
          // remove first and last quote
          .replace(/^"/, '')
          .replace(/"$/, '')
      )
    )
  )

type TableLayout = {
  type: 'table'
  columns: ({ name: string; class?: string } | string[])[]
  render: <T>(data: T) => undefined
  searchColumnsIndex?: number[]
}

type FullLayout = {
  type: 'full'
  render: () => string
}
type GridLayout = {
  type: 'grid'
  columns: ({ name: string; class?: string } | string[])[]
  render: <T>(data: T, ...rest: unknown[]) => undefined
}

export type PluginConfiguration = {
  name: string // name of the plugin
  icon: string // internal SVG icon - only <path> tags
  extra: string // any HTML element
  id: string // unique id for the plugin - lowercase, class name friendly
  layout: TableLayout | FullLayout | GridLayout
  render: (...data: unknown[]) => void
  init?: () => void
}

export const helpers = {
  updateTable,
  incrementNotificationBadge,
  clearNotificationBadge,
  replaceNewlinesToHTML,
  sanitizeStringQuotes,
  convertObjectToString,
  convertObjectToSanitizedDialogHTML,
  sanitizeHtml,
  createHTMLElement,
  createHTMLElements,
  showModal,
}
